
import {
	AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
	IDashboardSharedChartData, IDashboardSharedDimensionsViewModel,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimFacility, IDimension, IDimMDT } from "models/core";
import { IDashboardViewModel } from "./viewModel";

//Plots for this dashboard
import * as PrimarySite from "./primarySiteHierarchy";
import * as Morphology from "./morphology";
import * as Remoteness from "../../common/plotdata/remoteness";
import * as HHSOfResidence from "../../common/plotdata/hhsOfResidence";
import { FertilityPreservation, GPDetailsProvided, HadTreatment, Recurrence } from ".";

export type { IDashboardViewModel } from "./viewModel";

export type IDashboardSource = IDashboardViewModel[];

export interface IYouthCancerIndicatorConstants {
	totalYouthDiagnoses: number,
	upper95Percent: number,
	lower95Percent: number,
	upper98Percent: number,
	lower98Percent: number,
	statePercent: number
}

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
	defMastectomyArray?: Array<IYouthCancerIndicatorConstants>;
};

interface ILevel1ChartData {
	totalDiagnoses: number;
	totalDataQualityEligibleDiagnoses: number;
	facilities: IDimFacility[];
	hhses: IDimension[];
	mdts: IDimMDT[];
	month: IDimension[];
	year: IDimension[];
	// Export
	filteredReferralIdCsv: string;
}

// HACK: chop out the reference array from the shared interface 
type RestrictedSharedChartData = Omit<Omit<IDashboardSharedChartData, "facilities">, "hhses">;

interface ILevel2ChartData extends RestrictedSharedChartData {
	totalDiagnoses: number;
	primarySite: PrimarySite.IHierarchyPlotData;
	morphology: Morphology.IPlotData;
	remoteness: Remoteness.IPlotData;
	hhsOfResidence: HHSOfResidence.IPlotData;
	psychosocialCarePlanNum: { numerator, denominator: number, percentage: number };
    receivedPsychosocialCareNum: { numerator, denominator: number, percentage: number };
    hadFertilityDiscussionNum: { numerator, denominator: number, percentage: number };
    referralToFertilitySpecialistNum: { numerator, denominator: number, percentage: number };
    fertilityPreservationNum: { numerator, denominator: number, percentage: number };
    reviewedAtTreatmentMDTNum: { numerator, denominator: number, percentage: number };
    psychosocialSurvivorshipCarePlanNum: { numerator, denominator: number, percentage: number };
    medicalTreatmentPlanToGPNum: { numerator, denominator: number, percentage: number };
    enrolledInMedicalTrialNum: { numerator, denominator: number, percentage: number };
    enrolledInOtherResearchNum: { numerator, denominator: number, percentage: number };
	oneYearSurvival: { numerator, denominator: number, percentage: number };
	twoYearSurvival: { numerator, denominator: number, percentage: number };
	fiveYearSurvival: { numerator, denominator: number, percentage: number };
	fertilityPreservation: FertilityPreservation.IPlotData;
	diagnosisRecurrence: Recurrence.IPlotData;
	hadTreatment: HadTreatment.IPlotData;
	gpDetailsProvided: GPDetailsProvided.IPlotData;
	

}

export interface IChartData {
	level1: ILevel1ChartData;
	level2: ILevel2ChartData;
}

const YouthCancerFilterKeys: IFilterKeys = {
	//Filter keys for youth indicators
	DiagnosisYear: { key: "DiagnosisYear", label: "Diagnosis Year" },
	DiagnosisMonth: { key: "DiagnosisMonth", label: "Diagnosis Month" },
	MdtYear: { key: "MdtYear", label: "MDT Year" },
	MdtMonth: { key: "MdtMonth", label: "MDT Month" },
	PrimarySite: { key: "PrimarySite", label: "Primary site" },
	Morphology: { key: "Morphology", label: "Morphology" },
	Remoteness: { key: "Remoteness", label: "Rurality" },
	HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence" },
	HadTreatment: { key: "HadTreatment", label: "Had treatment"},
	GPDetailsProvided: { key: "GPDetailsProvided", label: "GP details provided"},
	FertilityPreservation: { key: "FertilityPreservation", label: "Fertility preservation"},
	DiagnosisRecurrence: { key: "DiagnosisRecurrence", label: "Cancer recurrence"}
}

export const DashboardFilterKeys: IFilterKeys = {
	...DashboardSharedFilterKey,
	...YouthCancerFilterKeys
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;